// TopBar — Quit + episode name + skeleton/free-drag toggles.
//
// (Platform port: local IndexedDB draft-on-quit is gone. Quit delegates to the
// shell's onQuit, which discards local viewer state and returns to assignment.
// An unsaved-edits confirm guards accidental loss.)

import * as React from 'react';
import {
  SecPoseDraftTransferControls,
  type SecPoseDraftTransferControlsProps,
} from '../../components/SecPoseDraftTransferControls';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { t } from '../../config/strings';
import { useStore } from '../../store';
import { editedFrameSet } from '../../store/edits';
import { KeypointLegend } from './KeypointLegend';

export interface TopBarProps {
  onQuit: () => void;
  draftTransfer?: SecPoseDraftTransferControlsProps | undefined;
}

export function TopBar({ onQuit, draftTransfer }: TopBarProps): React.ReactElement {
  const folderName = useStore((s) => s.folder?.name ?? '');
  const annotationMode = useStore((s) => s.annotationMode);
  const showSkeleton = useStore((s) => s.showSkeleton);
  const toggleSkeleton = useStore((s) => s.toggleSkeleton);
  const freeDrag = useStore((s) => s.freeDrag);
  const toggleFreeDrag = useStore((s) => s.toggleFreeDrag);
  const editsFrontLeft = useStore((s) => s.editsFrontLeft);
  const editsFrontRight = useStore((s) => s.editsFrontRight);
  const editsLeft = useStore((s) => s.editsLeft);
  const editsRight = useStore((s) => s.editsRight);

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const handleQuit = (): void => {
    const editedFrames = editedFrameSet({ editsFrontLeft, editsFrontRight, editsLeft, editsRight });
    if (editedFrames.size === 0) {
      onQuit();
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmQuit = (): void => {
    setConfirmOpen(false);
    onQuit();
  };

  return (
    <header className="flex h-12 shrink-0 items-center justify-between border-b border-border bg-background px-4">
      <div className="flex flex-1 justify-start">
        <Button variant="outline" size="sm" onClick={handleQuit}>
          {t.topBar.quit}
        </Button>
      </div>
      <div className="flex flex-1 items-center justify-center gap-2">
        <span className="truncate text-sm font-medium" data-testid="folder-name">
          {folderName}
        </span>
        {annotationMode === 'v2' && (
          <span
            className="shrink-0 rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-400"
            title={t.topBar.v2BadgeTitle}
            data-testid="mode-badge"
          >
            {t.topBar.v2Badge}
          </span>
        )}
      </div>
      <div className="flex flex-1 items-center justify-end gap-4">
        {draftTransfer && <SecPoseDraftTransferControls {...draftTransfer} />}
        <KeypointLegend />
        <label
          className="flex cursor-pointer items-center gap-2 text-sm"
          title={t.topBar.freeDragTitle}
        >
          <Checkbox checked={freeDrag} onChange={toggleFreeDrag} data-testid="free-drag-toggle" />
          <span className={freeDrag ? 'font-semibold text-amber-500' : ''}>
            {t.topBar.freeDrag}
          </span>
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={showSkeleton}
            onChange={toggleSkeleton}
            data-testid="skeleton-toggle"
          />
          <span>{t.topBar.skeleton}</span>
        </label>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.topBar.unsavedTitle}</DialogTitle>
            <DialogDescription>{t.topBar.unsavedDesc}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t.topBar.cancel}
            </Button>
            <Button variant="destructive" onClick={handleConfirmQuit}>
              {t.topBar.quit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
